import { memoize } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { IImage } from '../../../api/images';
import { isBadlyOriented } from '../../../entities/images/helpers';
import { useDebouncedCallback } from '../../helpers/hooks';
import { Panel } from '../panel/Panel';
import { Paragraph } from '../paragraph/Paragraph';
import DraggablePhotoTile from './DraggablePhotoTile';
import { IPhotoTileProps } from './PhotoTile';
import PhotoTileDragLayer from './PhotoTileDragLayer';

export interface IPhotoWallProps {
	images: IImage[];
	disabled: Array<IImage['id']>;
	onDelete: (images: IImage[]) => void;
	onView: (index: number) => void;
	onReorder: (images: IImage[]) => void;
}

const stateFromImages = (images: IImage[], disabled: IPhotoWallProps['disabled']) => {
	const sortedImages = [...images].sort((i1, i2) => i1.sort_order < i2.sort_order ? -1 : 1);
	return sortedImages.map((image, index): IPhotoTileProps => ({
		id: image.id,
		index,
		isDisabled: (disabled || []).includes(image.id),
		image
	}));
};

const TargetIndicator: React.FC<{ i: number, position?: 'before' | 'after', target: number }> = ({ i, target, position = 'after' }) => {
	const isTargeted = (
		(position === 'before' && target === -(i + 1)) ||
		(position === 'after' && (target === i + 1 || target === -(i + 2)))
	);

	return <div className={`target-indicator ${isTargeted ? `target-indicator--targeted` : ''}`} />;
};

const makeFindTiles = (tiles: IPhotoTileProps[]) =>
	memoize((id: string) => tiles.map((tile, index) => ({ tile, index })).filter(({ tile }) => tile.id === id)[0]);

export const PhotoWallNoBackend: React.FC<IPhotoWallProps> = (props) => {
	const { images, disabled } = props;

	const [tiles, setTiles] = useState<IPhotoTileProps[]>([]);

	// targetPosition is the POSITION (not index) the user targets while dragging a tile
	// 		targetPosition =  0 : no position is targeted (eg while not dragging)
	// 		targetPosition = +1 : AFTER the 1st tile
	// 		targetPosition = -1 : BEFORE the 1st tile
	//  	targetPosition = -2 : BEFORE the 2nd tile (ie the same as +1, AFTER the 1st tile)
	// AFTER vs BEFORE is important and depends on whether you're dragging upwards or downwards
	const [targetPosition, setTargetPosition] = useState(0);

	const onReorder = useDebouncedCallback(props.onReorder, 500);

	useEffect(() => {
		setTiles(stateFromImages(images, disabled));
	}, [images, disabled]);

	useEffect(() => {
		if (images.length === tiles.length) {
			const orderChanged = tiles.some((t, i) => t.image.sort_order !== i);
			if (orderChanged) {
				onReorder(tiles.map((t) => t.image));
			} else {
				onReorder.cancel();
			}
		}
	}, [tiles]);

	const removeTile = (id: string) => {
		onReorder.flush();
		props.onDelete(images.filter((image) => image.id === id));
	};

	const viewTile = (id: string) => {
		props.onView(tiles.map((image, index) => ({ image, index })).filter(({ image }) => image.id === id)[0].index);
	};

	const findTile = useMemo(() => makeFindTiles(tiles), [tiles]);

	const moveTile = (id: string, newIndex: number) => {
		const { index: oldIndex } = findTile(id);

		if (oldIndex === newIndex) return;

		const [start, end] = oldIndex < newIndex ? [oldIndex, newIndex] : [newIndex, oldIndex];

		const left = tiles.slice(0, start);
		const middle = tiles.slice(start, end + 1);
		const right = tiles.slice(end + 1);

		const newTiles = oldIndex < newIndex ?
			left.concat(middle, middle.shift() as IPhotoTileProps, right) :
			left.concat(middle.pop() as IPhotoTileProps, middle, right);

		setTiles(newTiles);
	};

	const heroPhotoIsBadlyOriented = tiles.length > 0 ? isBadlyOriented(tiles[0].image) : false;

	return (
		<div className="photo-wall">
			<div className="photo-wall__header">
				<Paragraph fillParent="mid">
					You have <strong>{images.length} {images.length === 1 ? 'photo' : 'photos'}.</strong>
					You can view and edit your photos here.
					Reorder them by <span className="mobile-hidden">dragging them into position, or</span> clicking the arrows.
					The first photo is the most important - guests see it in their search results.
				</Paragraph>
			</div>

			{heroPhotoIsBadlyOriented ? <Panel withIcon={true} type="error">
				Marketing channels request that your first photo is in landscape orientation.
				Select a landscape photo and drag it to the top left of the photo grid.
			</Panel> : null}

			<div className="photo-wall__tiles">
				<TargetIndicator i={0} position="before" target={targetPosition} />
				{tiles.map((tile, i) => (<React.Fragment key={tile.id}>
					<DraggablePhotoTile
						{...tile}
						moveTile={moveTile}
						findTile={findTile}
						onTargetPositionChange={setTargetPosition}
						onDeleteClick={() => removeTile(tile.id)}
						onViewClick={() => viewTile(tile.id)}
						onUpClick={i > 0 ? () => moveTile(tile.id, i - 1) : undefined}
						onDownClick={i < tiles.length - 1 ? () => moveTile(tile.id, i + 1) : undefined}
					/>
					<TargetIndicator i={i} target={targetPosition} />
				</ React.Fragment>))}
			</div>
		</div>
	);
};

export const PhotoWall: React.FC<IPhotoWallProps> = (props) => {
	// https://github.com/react-dnd/react-dnd/issues/1571
	return <DndProvider backend={HTML5Backend} context={window}>
		<PhotoWallNoBackend {...props} />
		<PhotoTileDragLayer />
	</DndProvider>;
};
